import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { GalleryImage } from "./GalleryImage";

const API_URL = "https://admin.bodiosyakdown.com/api/products";
const API_BASE = "https://admin.bodiosyakdown.com";

const CATEGORY_CONFIG = [
  { key: "women", label: "WOMEN" },
  { key: "men", label: "MEN" },
  { key: "accessories", label: "ACCESSORIES" },
];

function normalizeCategory(value) {
  return String(value || "").trim().toLowerCase();
}

function normalizeImageValue(value) {
  if (!value) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "object") {
    return value.url || value.image || value.path || value.src || value.location || "";
  }
  return "";
}

function toAbsoluteImageUrl(value) {
  const url = normalizeImageValue(value);
  if (!url) return "/placeholder.png";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/uploads/")) return `${API_BASE}${url}`;
  if (url.startsWith("uploads/")) return `${API_BASE}/${url}`;
  if (url.startsWith("/")) return `${API_BASE}${url}`;
  return `${API_BASE}/uploads/${url}`;
}

function getProductImages(product) {
  const rawImages = [];
  if (product?.mainImage) rawImages.push(product.mainImage);
  if (product?.main_image) rawImages.push(product.main_image);
  if (product?.image) rawImages.push(product.image);
  if (product?.imageUrl) rawImages.push(product.imageUrl);
  if (Array.isArray(product?.images)) rawImages.push(...product.images);
  if (Array.isArray(product?.gallery)) rawImages.push(...product.gallery);
  return [...new Set(rawImages.map(toAbsoluteImageUrl).filter(Boolean))];
}

function getProductTimestamp(product) {
  const dateValue =
    product?.createdAt || product?.created_at ||
    product?.updatedAt || product?.updated_at ||
    product?.date || null;
  const time = dateValue ? new Date(dateValue).getTime() : 0;
  if (Number.isFinite(time) && time > 0) return time;
  return Number(product?.id || 0);
}

function pickLatestProduct(products) {
  if (!Array.isArray(products) || products.length === 0) return null;
  return [...products].sort((a, b) => getProductTimestamp(b) - getProductTimestamp(a))[0];
}

export function ImageGallery() {
  const [products, setProducts] = useState([]);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const res = await axios.get(API_URL);
        const list = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.data)
          ? res.data.data
          : [];
        if (!cancelled) setProducts(list);
      } catch (error) {
        console.error("Failed to fetch gallery products:", error);
        if (!cancelled) setProducts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchProducts();
    return () => { cancelled = true; };
  }, []);

  const galleryItems = useMemo(() => {
    return CATEGORY_CONFIG.map((category) => {
      const categoryProducts = products.filter(
        (product) => normalizeCategory(product?.category) === category.key
      );
      const latestProduct = pickLatestProduct(categoryProducts);
      const productImages = latestProduct ? getProductImages(latestProduct) : [];
      return {
        key: category.key,
        label: category.label,
        href: `/category/${category.key}`,
        src: productImages[0] || "/placeholder.png",
        hoverSrc: productImages[1] || productImages[0] || "/placeholder.png",
      };
    });
  }, [products]);

  if (loading) {
    return (
      <div className="w-full px-4 md:px-8
                      mb-[40px] md:mb-[50px] lg:mb-[60px]
                      mt-[40px] md:mt-[60px] lg:mt-[80px]">
        <div className="max-w-[1543px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-14 lg:gap-[80px]">
          {CATEGORY_CONFIG.map((item) => (
            <div key={item.key} className="flex flex-col items-center animate-pulse">
              <div className="w-full max-w-[465px] aspect-[466/701] bg-gray-200" />
              <div className="mt-4 h-4 w-24 bg-gray-200 rounded" />
              <div className="mt-2 h-3 w-14 bg-gray-200 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 md:px-8
                    mb-[40px] md:mb-[50px] lg:mb-[134px]
                    mt-[40px] md:mt-[60px] lg:mt-[148px]">
      <div className="max-w-[1543px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-14 lg:gap-[80px]">
        {galleryItems.map((item, index) => {
          const isHovered = hoveredIndex === index;
          return (
            <div key={item.key} className="flex flex-col items-center text-center">
              <Link
                to={item.href}
                className="relative block w-full max-w-[465px] aspect-[466/701] overflow-hidden"
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                onTouchStart={() => setHoveredIndex(index)}
              >
                <GalleryImage
                  src={isHovered ? item.hoverSrc : item.src}
                  alt={item.label}
                  className="w-full h-full object-cover block"
                  priority={index < 2}
                />
              </Link>

              <div className="mt-[42px]">
                <Link
                  to={item.href}
                  className="block leading-none uppercase"
                  style={{ fontFamily: "'Githera Regular', sans-serif", fontSize: '22px', letterSpacing: '0.11em', fontWeight: 400 }}
                >
                  {item.label}
                </Link>
                <Link
                  to={item.href}
                  className="block mt-[14px] hover:underline text-center"
                  style={{
                    fontFamily: "'Rubik', sans-serif",
                    fontWeight: 300,
                    fontSize: '16px',
                    lineHeight: '17.7px',
                    letterSpacing: '0.07em',
                  }}
                >
                  View all
                </Link>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}